import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useReportFormatter } from 'dashboard/composables/useReportFormatter';

/**
 * A composable function for report metrics calculations and display.
 *
 * @param {string} [accountSummaryKey='getAccountSummary'] - The key for accessing account summary data.
 * @returns {Object} An object containing utility functions for report metrics.
 */
export function useReportMetrics(
  accountSummaryKey = 'getAccountSummary',
  summarFetchingKey = 'getAccountSummaryFetchingStatus'
) {
  const { t } = useI18n();
  const accountSummary = useMapGetter(accountSummaryKey);
  const fetchingStatus = useMapGetter(summarFetchingKey);
  const { duration } = useReportFormatter();

  /**
   * Sem amostra não é zero. A média de um período sem nenhum evento chega nula
   * do backend (média de nada), e o ponto do gráfico sem evento chega com a
   * contagem da base em zero. Nos dois casos, "0 Sec" afirmaria uma medida que
   * não houve.
   *
   * @param {number|string|null} value - O valor da métrica.
   * @param {number} [baseCount] - Quantos eventos o valor tem por base, quando se sabe.
   * @returns {boolean} Verdadeiro quando há medida para mostrar.
   */
  const hasSample = (value, baseCount) => {
    if (value === null || value === undefined || value === '') return false;
    return baseCount === undefined || baseCount === null || baseCount > 0;
  };

  /**
   * Calculates the trend percentage for a given metric.
   *
   * @param {string} key - The key of the metric to calculate trend for.
   * @returns {number} The calculated trend percentage, rounded to the nearest integer.
   */
  const calculateTrend = key => {
    if (!hasSample(accountSummary.value[key])) return 0;
    if (!accountSummary.value.previous[key]) return 0;
    const diff = accountSummary.value[key] - accountSummary.value.previous[key];
    return Math.round((diff / accountSummary.value.previous[key]) * 100);
  };

  /**
   * Checks if a given metric key represents an average metric type.
   *
   * @param {string} key - The key of the metric to check.
   * @returns {boolean} True if the metric is an average type, false otherwise.
   */
  const isAverageMetricType = key => {
    return [
      'avg_first_response_time',
      'avg_resolution_time',
      'reply_time',
    ].includes(key);
  };

  /**
   * Formats and displays a metric value based on its type.
   *
   * @param {string} key - The key of the metric to display.
   * @returns {string} The formatted metric value as a string.
   */
  const displayMetric = key => {
    const value = accountSummary.value[key];
    if (!hasSample(value)) return t('ANALYTICS_REPORTS.NO_DATA');
    if (isAverageMetricType(key)) {
      return duration(value);
    }
    return Number(value).toLocaleString();
  };

  return {
    calculateTrend,
    hasSample,
    isAverageMetricType,
    displayMetric,
    fetchingStatus,
  };
}
